import { useEffect, useState } from 'react'
import { doc, setDoc, onSnapshot, serverTimestamp } from 'firebase/firestore'
import { getFirebaseDb, isFirebaseAvailable } from '../lib/firebaseInit'
import type { User } from '../schemas/chat.schema'
import { CHAT_PATHSEGMENTS, CHATROOM_PATH } from '@/constants/chat'

interface PresenceData {
  userId: string
  isOnline: boolean
  lastSeen: number
}

export const usePresence = (
  roomId: string,
  currentUser: User,
  otherUserId: string
) => {
  const [otherUserOnline, setOtherUserOnline] = useState(false)
  const [lastSeen, setLastSeen] = useState<number | null>(null)

  useEffect(() => {
    if (!roomId || !currentUser.id) return

    if (!isFirebaseAvailable()) {
      console.warn(
        'Presence tracking is unavailable - Firebase not initialized'
      )
      return
    }

    const db = getFirebaseDb()
    if (!db) {
      console.warn(
        'Presence tracking is unavailable - Firebase not initialized'
      )
      return
    }

    const presenceRef = doc(
      db,
      CHATROOM_PATH,
      roomId,
      CHAT_PATHSEGMENTS.PRESENCE,
      currentUser.id
    )

    const setOnline = () => {
      if (!isFirebaseAvailable()) return
      const db = getFirebaseDb()
      if (!db) return

      setDoc(
        presenceRef,
        {
          userId: currentUser.id,
          isOnline: true,
          lastSeen: serverTimestamp(),
        },
        { merge: true }
      ).catch(err => {
        console.error('Error setting online status:', err)
      })
    }

    const setOffline = () => {
      if (!isFirebaseAvailable()) return
      const db = getFirebaseDb()
      if (!db) return

      setDoc(
        presenceRef,
        {
          userId: currentUser.id,
          isOnline: false,
          lastSeen: serverTimestamp(),
        },
        { merge: true }
      ).catch(err => {
        console.error('Error setting offline status:', err)
      })
    }

    setOnline()

    const intervalId = setInterval(setOnline, 30000)

    const handleVisibilityChange = () => {
      if (document.hidden) {
        setOffline()
      } else {
        setOnline()
      }
    }

    const handleBeforeUnload = () => {
      setOffline()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      clearInterval(intervalId)
      setOffline()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [roomId, currentUser.id])

  useEffect(() => {
    if (!roomId || !otherUserId) return

    if (!isFirebaseAvailable()) return

    const db = getFirebaseDb()
    if (!db) return

    const otherPresenceRef = doc(
      db,
      CHATROOM_PATH,
      roomId,
      CHAT_PATHSEGMENTS.PRESENCE,
      otherUserId
    )

    const unsubscribe = onSnapshot(
      otherPresenceRef,
      snapshot => {
        if (snapshot.exists()) {
          const data = snapshot.data() as PresenceData
          setOtherUserOnline(data.isOnline || false)

          if (data.lastSeen) {
            const timestamp =
              typeof data.lastSeen === 'number'
                ? data.lastSeen
                : 'toMillis' in (data.lastSeen as object)
                  ? (data.lastSeen as { toMillis: () => number }).toMillis()
                  : Date.now()
            setLastSeen(timestamp)
          }
        } else {
          setOtherUserOnline(false)
          setLastSeen(null)
        }
      },
      error => {
        console.error('Error listening to presence:', error)
        setOtherUserOnline(false)
        setLastSeen(null)
      }
    )

    return () => unsubscribe()
  }, [roomId, otherUserId])

  return {
    otherUserOnline,
    lastSeen,
  }
}
